import SectionHeading from "@/components/SectionHeading";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import CTASection from "@/components/CTASection";
import { images } from "@/data/business";
import { ShieldCheck, Wind, Zap, Droplets, Accessibility as A11yIcon, Sparkles, PackageOpen, EyeOff, Bus } from "lucide-react";

const SECTIONS = [
  { icon: Bus, title: "Treatment area", body: "A calm, private treatment area designed to feel welcoming — with plenty of natural light and comfortable seating for young patients." },
  { icon: Sparkles, title: "Dental chair & equipment", body: "Modern dental chair and clinical equipment suited to check-ups, cleans, fluoride treatments, sealants and X-rays where available." },
  { icon: ShieldCheck, title: "Sterilisation & infection control", body: "A dedicated area for instrument reprocessing and surface disinfection, following standard clinical infection-control practices." },
  { icon: PackageOpen, title: "Storage", body: "Secure, organised storage for consumables, PPE and clinical instruments — ready for the day's schedule." },
  { icon: EyeOff, title: "Privacy", body: "Treatment happens out of sight of classmates. Curtains, layout and scheduling all support privacy and dignity." },
  { icon: Wind, title: "Climate control", body: "Air-conditioning and ventilation help keep the clinical environment comfortable in Australian conditions." },
  { icon: Zap, title: "Power & water systems", body: "Powered by standard mains connection with on-board water and waste-water systems suited to a mobile clinical setting." },
  { icon: A11yIcon, title: "Accessibility considerations", body: "We work with each school to organise safe access to the van. Please let us know about any specific accessibility needs when you book." },
  { icon: Droplets, title: "Safety procedures", body: "Documented clinical, hygiene and workplace-safety procedures are followed by every team member on every visit." },
];

const DentalVan = () => {
  if (typeof document !== "undefined") document.title = "Our Dental Van — School Smiles Australia";
  return (
    <>
      <section className="container-page pt-16 md:pt-24 pb-8" data-testid="van-hero">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="eyebrow"><Bus size={14} strokeWidth={2.5}/> Our dental van</span>
            <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#14324A]">
              A clean, private clinic that comes to you
            </h1>
            <p className="mt-5 text-[#4B6B86] text-lg leading-relaxed">
              Our mobile dental clinic is designed to provide a calm, professional space for children — with proper privacy, climate control and clinical hygiene practices built in.
            </p>
          </div>
          <ImagePlaceholder src={images.dentalVan} alt="School Smiles Australia mobile dental van exterior" ratio="aspect-[16/9]" testId="van-hero-image"/>
        </div>
      </section>

      <section className="container-page py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="van-sections">
        {SECTIONS.map((s, i) => (
          <div key={s.title} className="card-soft p-8" data-testid={`van-section-${i}`}>
            <div className="w-12 h-12 rounded-xl bg-[#F4F9FF] text-[#1677C8] inline-flex items-center justify-center">
              <s.icon size={22} strokeWidth={2.5}/>
            </div>
            <h3 className="mt-4 font-display font-semibold text-lg text-[#14324A]">{s.title}</h3>
            <p className="mt-2 text-sm text-[#4B6B86] leading-relaxed">{s.body}</p>
          </div>
        ))}
      </section>

      <section className="container-page py-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="van-gallery">
        <ImagePlaceholder src={images.treatmentArea} alt="Interior treatment area" testId="van-image-1"/>
        <ImagePlaceholder src={images.clinicRoom} alt="Clinical setup" testId="van-image-2"/>
        <ImagePlaceholder src={images.friendlyDentist} alt="Dentist with a young patient" testId="van-image-3"/>
      </section>

      <section className="container-page py-6" data-testid="van-disclaimer">
        <div className="rounded-3xl border border-[#E5F0FA] bg-[#F4F9FF] p-8">
          <p className="text-sm text-[#4B6B86] leading-relaxed">
            The features described above are provided for general information. We do not claim compliance with any particular standard unless that standard has been separately verified and documented. Detailed compliance information can be provided on request.
          </p>
        </div>
      </section>

      <div className="container-page"><CTASection testIdPrefix="van-cta" /></div>
    </>
  );
};

export default DentalVan;
